package dev.lingvo.site.components.editor

internal fun EditorWorkspaceState.openModal(
    surface: EditorModalSurface,
    invoker: EditorFocusRef,
): EditorTransition {
    require(surface != EditorModalSurface.NONE)
    val initialFocusId = when (surface) {
        EditorModalSurface.CLEAR_CONFIRMATION -> EDITOR_CLEAR_MODAL_INITIAL_FOCUS_ID
        EditorModalSurface.SHORTCUTS -> EDITOR_SHORTCUTS_MODAL_INITIAL_FOCUS_ID
        EditorModalSurface.BULK_CONFIRMATION -> EDITOR_BULK_MODAL_INITIAL_FOCUS_ID
        EditorModalSurface.NONE -> error("A modal surface is required")
    }
    val nextState = copy(
        modal = surface,
        modalReturnFocus = invoker,
        overflowOpen = false,
        overflowReturnFocus = null,
        focusAfterQueueRows = null,
    )
    return nextState.emitOrDeferFocus(
        EditorDeferredFocus(EditorFocusRef.ElementId(initialFocusId), invoker),
        restoreQueue = false,
    )
}
internal fun EditorWorkspaceState.closeModal(): EditorTransition {
    if (modal == EditorModalSurface.NONE) return EditorTransition(this)
    val deferredFocus = modalReturnFocus?.let { EditorDeferredFocus(it, null) }
    return copy(
        modal = EditorModalSurface.NONE,
        modalReturnFocus = null,
        focusAfterQueueRows = null,
    ).emitOrDeferFocus(deferredFocus, restoreQueue = false)
}

internal fun EditorWorkspaceState.openOverflow(invoker: EditorFocusRef): EditorTransition = copy(
    overflowOpen = true,
    overflowReturnFocus = invoker,
    focusAfterQueueRows = null,
    focusAfterMeasurement = null,
).asTransition()

internal fun EditorWorkspaceState.closeOverflow(): EditorTransition {
    if (!overflowOpen) return EditorTransition(this)
    val target = overflowReturnFocus ?: EditorFocusRef.ElementId(EDITOR_OVERFLOW_TRIGGER_ID)
    return copy(
        overflowOpen = false,
        overflowReturnFocus = null,
        focusAfterQueueRows = null,
    ).emitOrDeferFocus(
        EditorDeferredFocus(target, EditorFocusRef.ElementId(EDITOR_OVERFLOW_TRIGGER_ID)),
        restoreQueue = false,
    )
}

internal fun EditorWorkspaceState.closeTopSurface(
    selectedRow: EditorFocusRef.QueueRow?,
    activeTarget: EditorFocusRef.Target?,
    utilityActionOrigin: EditorUtilityActionOrigin = EditorUtilityActionOrigin.KEYBOARD,
): EditorTransition = when {
    modal != EditorModalSurface.NONE -> closeModal()
    overflowOpen -> closeOverflow()
    queueMode == EditorQueueMode.FILTERS -> closeFilters()
    utility != EditorUtilitySurface.NONE -> closeUtility(activeTarget, utilityActionOrigin)
    measuredLayoutDisposition == EditorMeasuredLayoutDisposition.WORKBENCH -> {
        selectedRow?.let { requestFocus(it, EditorFocusRef.Search) } ?: EditorTransition(this)
    }
    primaryView == EditorPrimaryView.EDITOR -> showQueue(selectedRow)
    else -> EditorTransition(this)
}
